import { Line, curveBasis, line as d3Line } from 'd3-shape';

import { Pos } from '../LineWidget.types';

/**
 * Controls the smoothness of the curve.
 * Smaller values produce tighter curves, while larger values create smoother curves.
 * Represents a fraction of the line length between two points.
 */
const T = 1 / 10;
const MAX_OFFSET = 50;
const OFFSET_FACTOR = 2 / 3;

/**
 * Computes control points for creating smooth curves between given points.
 * Assumes that the line segment only contains right angles (i.e. stepped line)
 * Points are passed into d3 curveBasis to generate a smooth curve.
 *
 * @param {Pos[]} posList - List of points to create smooth curves.
 * @returns {Pos[]} - List of control points for smooth curve generation.
 */
export const computeControlPoints = (posList: Pos[]) => {
  if (posList.length <= 2) return posList;

  const curvePoints = [];

  // Add first point
  curvePoints.push(posList[0]);

  // Iterate through each point
  for (let i = 0; i < posList.length - 1; i++) {
    const p1 = posList[i];
    const p2 = posList[i + 1];

    // Calculate midpoint of the line segment
    const midX = (p1.xPx + p2.xPx) / 2;
    const midY = (p1.yPx + p2.yPx) / 2;

    // Add additional points for the first and last segments based on 't'
    if (i === 0) {
      // compute start point for first segment
      const startX = p1.xPx + (midX - p1.xPx) * T;
      const startY = p1.yPx + (midY - p1.yPx) * T;
      curvePoints.push({ xPx: startX, yPx: startY });
      curvePoints.push({ xPx: midX, yPx: midY });
    }

    // Add additional points for the middle segments based on the direction of the previous and next segments
    // If a C-shaped section is found, add additional points to make the curve smoother
    if (i > 0 && i < posList.length - 2) {
      const prev = posList[i - 1];
      const next = posList[i + 2];
      const prevDx = p1.xPx - prev.xPx;
      const prevDy = p1.yPx - prev.yPx;
      const nextDx = next.xPx - p2.xPx;
      const nextDy = next.yPx - p2.yPx;

      // Get the directions of the previous and next line segments
      const prevXDir = Math.sign(Math.round(prevDx));
      const prevYDir = Math.sign(Math.round(prevDy));
      const nextXDir = Math.sign(Math.round(nextDx));
      const nextYDir = Math.sign(Math.round(nextDy));

      // Check if both vectors are going in the same direction
      if (prevXDir === nextXDir && prevYDir === nextYDir) {
        // They are going in the same direction so don't need to adjust the curve point - not a C-shape
        curvePoints.push({ xPx: midX, yPx: midY });
      } else {
        // They are going in different directions so need to adjust the curve point - C-shape
        // Extend the control point outwards from the midpoint of the line segment
        // The length of the extension is based on the length of the line segment up to a max of MAX_OFFSET
        const dx = p2.xPx - p1.xPx;
        const dy = p2.yPx - p1.yPx;
        const lineLength = Math.sqrt(dx * dx + dy * dy);
        const offset = Math.min(lineLength / 2, MAX_OFFSET);
        const xOffset = prevXDir * offset;
        const yOffset = prevYDir * offset;

        // Add the additional control points based on the offset
        curvePoints.push({
          xPx: p1.xPx + xOffset * OFFSET_FACTOR,
          yPx: p1.yPx + yOffset * OFFSET_FACTOR,
        });
        curvePoints.push({
          xPx: midX + xOffset,
          yPx: midY + yOffset,
        });
        curvePoints.push({
          xPx: p2.xPx + xOffset * OFFSET_FACTOR,
          yPx: p2.yPx + yOffset * OFFSET_FACTOR,
        });
      }
    }

    if (i === posList.length - 2) {
      // compute end point for last segment
      const endX = p2.xPx - (p2.xPx - midX) * T;
      const endY = p2.yPx - (p2.yPx - midY) * T;
      curvePoints.push({ xPx: midX, yPx: midY });
      curvePoints.push({ xPx: endX, yPx: endY });
    }
  }

  // Add last point
  curvePoints.push(posList[posList.length - 1]);
  return curvePoints;
};

/**
 * Generate a curved path from a list of points using d3 curveBasis
 *
 * @param posList
 * @returns path string to be passed into <path d={path} />
 */
export const parsePosListToCurvedPath = (controlPosList: Pos[]): string => {
  let path = '';

  if (!controlPosList.length || controlPosList.length < 2) return path;

  const d3Curve: Line<Pos> = d3Line<Pos>()
    .x((d: Pos) => d.xPx)
    .y((d: Pos) => d.yPx)
    .curve(curveBasis) as Line<Pos>;

  path = d3Curve(controlPosList) || '';
  return path;
};

/**
 * Compute a pos list that forms a right angle between 2 points
 * If a straight line, return the 2 points
 * If a diagonal line, return 3 points forming a right angle curving CW from start to end
 *
 * @param startPos
 * @param endPos
 * @returns
 */
export const computePosListFrom2Points = (startPos: Pos, endPos: Pos): Pos[] => {
  // if horizontal or vertical line, return the 2 points
  if (startPos.xPx === endPos.xPx || startPos.yPx === endPos.yPx) {
    return [startPos, endPos];
  }

  const dx = endPos.xPx - startPos.xPx;
  const dy = endPos.yPx - startPos.yPx;

  const xDir = Math.sign(Math.round(dx));
  const yDir = Math.sign(Math.round(dy));
  const isSameSign = xDir === yDir;

  // Calculate the coordinates for the midpoint
  const midX = isSameSign ? endPos.xPx : startPos.xPx;
  const midY = isSameSign ? startPos.yPx : endPos.yPx;

  return [startPos, { xPx: midX, yPx: midY }, endPos];
};
